import './SharedStyles.css';

import { mergeDeep } from '@toggle/helpers';
import * as Types from 'echarts/types/dist/echarts';

import { formatDecimalPlaces } from '~/components/table/utils';
import { getThemeColor } from '~/design-tokens/themes/theme-utils';
import { ThemeName } from '~/design-tokens/themes/themes';

import { Y_AXIS_LABEL_WIDTH, Y_AXIS_PADDING } from './apache-config';

const skipLabelMerge = ['heatmap', 'treemap'];

interface TooltipFormatterArgs {
  isShowTitle: boolean;
  isShowMarker: boolean;
  params: Types.DefaultLabelFormatterCallbackParams;
}

export const tooltipFormatter = ({
  isShowTitle,
  isShowMarker,
  params,
}: TooltipFormatterArgs) => {
  if (Array.isArray(params)) {
    const xAxisName = params[0].axisValueLabel || params[0].axisValue;
    const contentInfo = params.reduce((acc, param) => {
      const marker = isShowMarker ? param.marker : '';
      const numericValue = Number(param.value);
      const value = isNaN(numericValue)
        ? '-'
        : formatDecimalPlaces(numericValue);

      return `
        ${acc}
        <div class="tooltip">
            <div>${marker}${param.seriesName}</div>
            <div class="value">${value}</div>
        </div>
      `;
    }, '');

    return `${isShowTitle ? xAxisName : ''}${contentInfo}`;
  }

  return `
        ${isShowTitle ? params.seriesName : ''}
        <div class="tooltip">
            <div>${isShowMarker ? params.marker : ''}${params.name}</div>
            <div class="value">${formatDecimalPlaces(
              Number(params.value)
            )}</div>
        </div>
    `;
};

interface MergeYAxisArgs {
  theme: ThemeName;
  customYAxis: Types.YAXisComponentOption | Types.YAXisComponentOption[];
  isModal?: boolean;
  yAxisLabelWidth?: number;
}

export const mergeYAxis = ({
  theme,
  customYAxis,
  isModal,
  yAxisLabelWidth,
}: MergeYAxisArgs) => {
  const axisLabelWidth = yAxisLabelWidth ?? Y_AXIS_LABEL_WIDTH;

  const defaultSetting = {
    axisLabel: {
      fontSize: 12,
      fontFamily: 'Roboto Mono',
      width: axisLabelWidth,
      overflow: 'truncate',
      show: true,
    },
    tooltip: {
      show: true,
    },
    axisTick: {
      show: false,
      lineStyle: {
        color: getThemeColor('--border-default', theme),
      },
    },
    scale: true,
    splitLine: {
      show: true,
      lineStyle: {
        color: getThemeColor('--border-default', theme),
        width: 0.5,
      },
    },
    ...(isModal
      ? {}
      : {
          nameTruncate: {
            maxWidth: axisLabelWidth - Y_AXIS_PADDING,
            ellipsis: '...',
          },
        }),
  };

  if (Array.isArray(customYAxis)) {
    return customYAxis.map((yAxis, index) =>
      mergeDeep(defaultSetting, {
        ...yAxis,
        nameTextStyle: {
          align: index ? 'left' : 'right',
        },
      })
    );
  }

  return mergeDeep(defaultSetting, customYAxis);
};

export const mergeSeriesLabel = ({
  series,
  labelConfig,
}: {
  series?: Types.SeriesOption | Types.SeriesOption[];
  labelConfig: Partial<Types.SeriesOption>;
}) => {
  if (!series) {
    return undefined;
  }

  if (Array.isArray(series)) {
    return series.map((s: Types.SeriesOption) => {
      if (s.type && skipLabelMerge.includes(s.type)) {
        return s;
      }

      return mergeDeep(s, labelConfig);
    });
  }

  if (series.type && skipLabelMerge.includes(series.type)) {
    return series;
  }

  return mergeDeep(series, labelConfig);
};
